import moment from 'moment';
import { TFunction } from 'i18next';

import { DatePresetId } from './constants';

/** Placeholder shown wherever a value is null/undefined (tz.md §5). */
export const DASH = '—';

export interface IDateRange {
  from: string | null;
  to: string | null;
}

/** Resolves a date preset into an ISO `YYYY-MM-DD` range (`all` → unbounded). */
export const presetRange = (id: DatePresetId): IDateRange => {
  if (id === 'all') {
    return { from: null, to: null };
  }
  const to = moment();
  const from =
    id === '30d'
      ? to.clone().subtract(30, 'days')
      : id === '90d'
      ? to.clone().subtract(90, 'days')
      : to.clone().subtract(1, 'year');
  return { from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') };
};

/** Renders any nullable number, falling back to the dash placeholder. */
export const fmtNum = (value?: number | null): string =>
  value === null || value === undefined ? DASH : String(value);

/** Renders a nullable integer percentage as `42%` or the dash placeholder. */
export const fmtPct = (value?: number | null): string =>
  value === null || value === undefined ? DASH : `${value}%`;

/**
 * Formats a time-to-sign value given in hours: minutes under 1h, hours under
 * 1d, otherwise days. Ported from `fmtTTS` in index.html.
 */
export const fmtTTS = (
  hours: number | null | undefined,
  t: TFunction,
): string => {
  if (hours === null || hours === undefined || Number.isNaN(hours)) {
    return DASH;
  }
  if (hours < 1) {
    return `${Math.round(hours * 60)}${t('pages.pandadocAnalytics.units.min')}`;
  }
  if (hours < 24) {
    return `${Math.round(hours * 10) / 10}${t(
      'pages.pandadocAnalytics.units.hour',
    )}`;
  }
  return `${Math.round((hours / 24) * 10) / 10}${t(
    'pages.pandadocAnalytics.units.day',
  )}`;
};

/** Formats an ISO date string (`2024-06-01`) or returns the dash placeholder. */
export const fmtDate = (value?: string | null): string => {
  if (!value) {
    return DASH;
  }
  return String(value).slice(0, 10);
};

type NumericList = (number | null | undefined)[] | null | undefined;

/** Sums a nullable list of nullable numbers, treating gaps as 0. */
export const sumList = (values?: NumericList): number =>
  (values ?? []).reduce<number>((acc, v) => acc + (v ?? 0), 0);

/** Counts the non-null entries in a list (e.g. filled timeline buckets). */
export const countDefinedList = (values?: NumericList): number =>
  (values ?? []).filter((v) => v !== null && v !== undefined).length;
